import React, { useEffect, useState } from "react";
import { takeUntil } from "rxjs/operators";
import PropTypes from "prop-types";
import { Subject } from "rxjs";
import { connect } from "react-redux";
import ClassificationModal from "../components/ClassificationModal/ClassificationModal";
import ClipboardCopier from "../../../components/atoms/ClipboardCopy";
import ClinvarButton from "../../clinvar-submissions/ClinvarButton";
import FavoriteButton from "../../favorites/FavoriteButton";
import PubmedForm from "../../../components/PubmedUserEntries/PubmedForm";
import { getUserFlagSelector } from "../../../state/selectors/CommunityContributions.selectors";
import {
  constructVariantQueryParams,
  eventTrack,
} from "../../../common/helpers/helpers";
import { ANALYTICS_CATEGORIES } from "../../../components/cards/common/constants";
import {
  getApiUrl,
  getGenome,
  getObjectId,
  getQueryType,
} from "../../../state/selectors/general.selectors";
import apiClient from "../../../services/apiClient";
import { setVariantFlags } from "../../../state/actions/genericActions";
import { QUERY_TYPES } from "../../../common/helpers/Constant";

function VariantInfoIcons(props) {
  const {
    queryType,
    userFlag,
    genome,
    apiUrl,
    variantQueryMode,
    query,
    objectId,
    dispatchVariantFlags,
  } = props;
  const [shortUrl, setShortUrl] = useState("");
  const isCNV = queryType === QUERY_TYPES.CNV;
  const isSingleRead = queryType === QUERY_TYPES.SINGLE_READ;
  const isGene = queryType === QUERY_TYPES.GENE;
  const isVarid = queryType === QUERY_TYPES.VARID;
  const isPosition = queryType === QUERY_TYPES.POSITION;
  const isTranscript = queryType === QUERY_TYPES.TRANSCRIPT;
  const isSomatic = variantQueryMode?.annotation_mode === QUERY_TYPES.SOMATIC;
  const genomeParam = isVarid ? "" : `/${genome.id}`;

  useEffect(() => {
    const destroyed$ = new Subject();
    const infoQueryType = queryType === "gene" ? "gene" : "variant";
    const queryParams = constructVariantQueryParams(variantQueryMode);
    const url = `info/${infoQueryType}/${objectId}/${query}?${queryParams}`;

    if (isCNV || isSingleRead || isPosition || isTranscript) {
      return () => {};
    }

    apiClient
      .get({ url })
      .pipe(takeUntil(destroyed$))
      .subscribe(
        ({ flags, short_url }) => {
          const variantFlags = Array.isArray(flags) ? flags : [];
          const currentUserFlag = variantFlags
            .reverse()
            .find((x) => x.user_flag);

          dispatchVariantFlags({ variantFlags, userFlag: currentUserFlag });
          setShortUrl(short_url.replace("https://", "http://"));
        },
        (err) => {
          console.error("While getting variant info(shortUrl etc.):", err);
        }
      );

    return () => destroyed$.next();
  }, [apiUrl, queryType, objectId, query]);

  function getApiLink() {
    if (isCNV) {
      const link = `${apiUrl}/lookup/cnv/${query}${genomeParam}?add-all-data=1`;
      window.open(link);
      return;
    }

    const apiQueryType = queryType === "gene" ? "gene/" : "";
    const addACMG = isGene ? "" : "&add-ACMG-annotation=1";
    const addAmp = isSomatic ? "&add-AMP-annotation=1" : "";
    const queryParams = constructVariantQueryParams(variantQueryMode);

    const link = `${apiUrl}/lookup/${apiQueryType}${query}${genomeParam}?add-all-data=1${addACMG}${addAmp}${queryParams}`;
    window.open(link);
  }

  return (
    <div className="no-print tw-text-xs tw-font-bold tw--mr-2 tw-flex-grow">
      <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-items-end">
        <div className="tw-flex-grow" />
        {queryType !== "position" && queryType !== "transcript" && (
          <>
            {queryType !== "cnv" && (
              <div>
                <ClinvarButton
                  type="inverted"
                  analyticsLocation="Analysis Page - buttons top"
                />
              </div>
            )}
            {queryType !== "gene" && queryType !== "cnv" && (
              <PubmedForm className="saph-white-ghost tw-font-bold tw-bg-transparent" />
            )}

            {queryType !== "gene" && queryType !== "cnv" && (
              <ClassificationModal
                addToHelpTour={true}
                userFlag={userFlag}
                analyticsLocation={
                  ANALYTICS_CATEGORIES.ANALYSIS_PAGE_HEADER_BUTTONS
                }
              />
            )}
            {queryType !== "cnv" && (
              <div>
                <span className="saph-white-ghost">
                  <ClipboardCopier text={shortUrl}>
                    <span
                      className="tw-font-bold"
                      id="copyShortUrl"
                      data-tooltip={shortUrl}
                    >
                      <i className="tw-mr-1 fa fa-clipboard" />
                      Share
                    </span>
                  </ClipboardCopier>
                </span>
              </div>
            )}
            <div>
              <span
                className="saph-white-ghost"
                onClick={() => {
                  eventTrack(
                    ANALYTICS_CATEGORIES.ANALYSIS_PAGE_HEADER_BUTTONS,
                    "API Link",
                    ""
                  );
                  getApiLink();
                }}
              >
                <i className="tw-mr-1 fa fa-link" />
                API Link
              </span>
            </div>
            {queryType !== "cnv" && <FavoriteButton shortUrl={shortUrl} />}
          </>
        )}
      </div>
    </div>
  );
}

VariantInfoIcons.propTypes = {
  queryType: PropTypes.string,
  userFlag: PropTypes.shape({}),
  genome: PropTypes.shape({ id: PropTypes.number }),
  variantQueryMode: PropTypes.shape({ annotation_mode: PropTypes.string }),
  apiUrl: PropTypes.string,
  query: PropTypes.string,
  objectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dispatchVariantFlags: PropTypes.func.isRequired,
};

VariantInfoIcons.defaultProps = {
  queryType: "",
  userFlag: null,
  genome: {},
  variantQueryMode: null,
  apiUrl: "",
  query: "",
  objectId: 0,
};

const mapStateToProps = (state) => ({
  queryType: getQueryType(state),
  userFlag: getUserFlagSelector(state),
  genome: getGenome(state),
  apiUrl: getApiUrl(state),
  query: state.query,
  variantQueryMode: state.variantQueryMode,
  objectId: getObjectId(state),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchVariantFlags: (params) => dispatch(setVariantFlags(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(VariantInfoIcons);
